import { Injectable } from '@angular/core';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { List, MediaType } from '@epicuro-next/models/engagement';
import { plural } from '@epicuro-next/platform/i18n';
import {
  ListEntitiesState,
  LIST_ENTITIES_STATE_KEY,
  selectListEntitySearchSortType,
  selectOpenedListFromListEntity,
  selectOpenedListId,
} from '@epicuro-next/state/list-entities';
import {
  productListFeatureKey,
  ProductListState,
  selectShopTheListLoading,
  updateProductList,
} from '@epicuro-next/state/product-list-state';
import {
  getValue,
  isNonNullish,
  isNullish,
  setValue,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, take, takeUntil, tap } from 'rxjs/operators';

interface State {
  [LIST_ENTITIES_STATE_KEY]: ListEntitiesState;
  [productListFeatureKey]: ProductListState;
}

export const ADD_MEDIA_STRING = $localize`:ProductLists|Add merchandising Text@@addMerchandisingText:Add merchandising`;
export const CHANGE_MEDIA_STRING = $localize`:ProductLists|Change merchandising Text@@changeMerchandisingText:Change merchandising`;
@Injectable()
export class OpenedListDataProviderService implements AbstractDataProvider {
  private bindings = new Map<string, Observable<unknown>>();
  private destroy$ = new Subject<void>();

  public list$ = this.store.pipe(
    select(selectOpenedListFromListEntity),
    filter(isNonNullish),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$),
  );

  public listId$ = this.store.pipe(
    select(selectOpenedListId),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private isVideoMedia$ = this.list$.pipe(
    map((list) => {
      return (
        list?.mainMedia?.mediaType === MediaType.Video ||
        list?.mainMedia?.mediaType === MediaType.Directshow
      );
    }),
  );

  private promotedViaEmail$ = this.list$.pipe(
    map((list) => {
      return !!list.recipients?.emails?.length;
    }),
  );

  private productCountText$ = this.list$.pipe(
    map((list) => {
      if (!list) return '';

      const { availableProductsCount } = list;

      if (isNullish(availableProductsCount)) return '';

      return plural(
        availableProductsCount,
        {
          1: $localize`:list header|single available product count@@listHeaderSingleAvailableProductCount:${availableProductsCount} available product`,
        },
        $localize`:list header|available product count@@listHeaderAvailableProductCount:${availableProductsCount} available products`,
      );
    }),
  );

  private epicShowTooltip$ = this.list$.pipe(
    map((list) => {
      if (isNullish(list)) return null;

      if (!list.mainMedia) {
        return $localize`:ProductLists|
          Disable Create Epic Show No Video Text@@disableCreateEpicShowNoVideoText:Please upload a video to create an epic show`;
      }

      if (
        list.mainMedia.mediaType === MediaType.Youtube ||
        list.mainMedia.mediaType === MediaType.Vimeo
      )
        return $localize`:ProductLists|
          Disable Create Epic Show Unsupported Text@@disableCreateEpicShowUnsupportedText:Youtube and Vimeo are not supported`;
      else {
        if (
          list.mainMedia.mediaType === MediaType.Video ||
          list.mainMedia.mediaType === MediaType.Directshow
        ) {
          if (list.availableProductsCount === 0) {
            return $localize`:ProductLists|
              Disable Create Epic Show No Products Text@@disableCreateEpicShowNoProductsText:Please add products to create an epic show`;
          } else
            return $localize`:ProductLists|Make Epic Show Text@@makeEpicShowPossibleText:Make epic show`;
        } else {
          return $localize`:ProductLists|
            Disable Create Epic Show No Video Text@@disableCreateEpicShowNoVideoText:Please upload a video to create an epic show`;
        }
      }
    }),
  );

  private mediaEditText$ = this.list$.pipe(
    map((list) => {
      if (isNullish(list)) return null;

      if (!list.mainMedia) {
        return ADD_MEDIA_STRING;
      }

      return CHANGE_MEDIA_STRING;
    }),
  );

  private sortType$ = this.store.pipe(select(selectListEntitySearchSortType()));

  private readonly shopTheListLoading$ = this.store.pipe(
    select(selectShopTheListLoading),
  );

  constructor(private store: Store<State>) {
    this.initBindings();
  }

  public read<T>(property: string): Observable<T> {
    let observable = this.bindings.get(property);

    if (observable) {
      return observable as Observable<T>;
    }

    observable = this.list$.pipe(
      map((list: List) => {
        const result = getValue(list, property);

        return isNullish(result) ? null : result;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.destroy$),
    );

    this.bindings.set(property, observable);

    return observable as Observable<T>;
  }

  public write(value: unknown, property: string) {
    this.list$
      .pipe(
        map((list) => {
          const newValue = { ...list };
          setValue(newValue, property, value);

          return newValue;
        }),
        tap((list) => {
          this.store.dispatch(updateProductList({ list }));
        }),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private initBindings() {
    const bindingsEntries: [string, Observable<unknown>][] = [
      ['isVideoMedia', this.isVideoMedia$],
      ['promotedViaEmail', this.promotedViaEmail$],
      ['listId', this.listId$],
      ['epicShowTooltip', this.epicShowTooltip$],
      ['sortType', this.sortType$],
      ['mediaEditText', this.mediaEditText$],
      ['productCountText', this.productCountText$],
      ['shopTheListLoading', this.shopTheListLoading$],
    ];

    bindingsEntries.forEach(([propertyName, observer]) => {
      this.initBinding(propertyName, observer);
    });
  }

  private initBinding(propertyName: string, observer: Observable<unknown>) {
    this.bindings.set(
      propertyName,
      observer.pipe(
        shareReplay({ bufferSize: 1, refCount: true }),
        takeUntil(this.destroy$),
      ),
    );
  }
}
